/**
 * browse-analytics.ts — first-party analytics event names + thin emitters for
 * the marketplace browse page (STORY-003-005, "Analytics events" block).
 *
 * The events are DEFINED here and emitted onto the shared in-memory queue
 * (`@/lib/analytics-queue`), but no drainer flushes them until EPIC-009 —
 * exactly the contract `trackContactClick` (STORY-003-003) follows. First-party
 * only (no third-party trackers — REQ-PRD-§11.2-C) and PII-free by construction
 * (only opaque ids; never a phone, email or address — REQ-TRD-§14.6-A).
 *
 * The browse events the story prescribes also want a small filter SNAPSHOT
 * (`viewed` / `filter_applied`) and a grid position (`card_clicked`). The shared
 * `AnalyticsEventProps` contract (`@/lib/analytics-queue`) is intentionally
 * narrow today — opaque ids + a channel enum — and that module is owned by
 * STORY-003-003 (out of this story's allowed-touch). EPIC-009 widens the union
 * AND attaches the drainer; until then this module emits ONLY the props the
 * shared contract already accepts (`sellerId` / `productId`) and exposes the
 * snapshot type + names so the page computes them and EPIC-009 wires the rest.
 *
 * Emit from inside event handlers / mount effects — NOT the render path — so
 * React 19 strict mode does not double-count.
 */
import { enqueueAnalyticsEvent } from "@/lib/analytics-queue";

/** Dot-namespaced event names (story "Analytics events"). */
export const BROWSE_EVENTS = {
  viewed: "marketplace.browse.viewed",
  filterApplied: "marketplace.browse.filter_applied",
  loadMore: "marketplace.browse.load_more",
  cardClicked: "marketplace.product.card_clicked",
} as const;

/**
 * A PII-free snapshot of the active filters the story attaches to the `viewed`
 * and `filter_applied` events. Booleans / counts / an enum-ish sort only —
 * never a free-text value or a raw id list. EPIC-009 consumes this once the
 * shared `AnalyticsEventProps` union is widened to carry it.
 */
export interface BrowseFilterSnapshot {
  hasCategory: boolean;
  marketCount: number;
  inStockOnly: boolean;
  hasPriceMin: boolean;
  hasPriceMax: boolean;
  sort: string;
}

/**
 * `marketplace.browse.viewed` — page mount. The filter snapshot is computed by
 * the caller (and is PII-free); it is carried in the event NAME today and the
 * full payload lands when EPIC-009 widens `AnalyticsEventProps`. Accepting the
 * snapshot here keeps the call site honest and the signature stable.
 */
export function trackBrowseViewed(_snapshot: BrowseFilterSnapshot): void {
  enqueueAnalyticsEvent(BROWSE_EVENTS.viewed, {});
}

/** `marketplace.browse.filter_applied` — any filter change. */
export function trackBrowseFilterApplied(_snapshot: BrowseFilterSnapshot): void {
  enqueueAnalyticsEvent(BROWSE_EVENTS.filterApplied, {});
}

/** `marketplace.browse.load_more` — "Load more" pagination click. */
export function trackBrowseLoadMore(): void {
  enqueueAnalyticsEvent(BROWSE_EVENTS.loadMore, {});
}

/**
 * `marketplace.product.card_clicked` — card click. Carries the opaque
 * product + seller ids (both accepted by the shared contract today); the grid
 * position the story prescribes lands with the EPIC-009 union widening.
 */
export function trackProductCardClicked(
  productId: string,
  sellerId: string,
  _positionInGrid: number,
): void {
  enqueueAnalyticsEvent(BROWSE_EVENTS.cardClicked, { productId, sellerId });
}
